import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActorDisplayName } from '@safetyhub/shared';
import { Button, Modal } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { useToastStore } from '../stores/toast';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

type TargetType = 'all' | 'organization' | 'users';
type Priority = 'normal' | 'important';

/** 대상 후보 목록 상한 — 넘으면 «검색으로 좁히라»고 알린다(전체를 한 목록에 펼치지 않는다) */
const MESSAGE_TARGET_LIMIT = 50;

export default function MessageSend() {
  const { showToast } = useToastStore();
  const [targetType, setTargetType] = useState<TargetType>('all');
  // 조직 대상 발송의 targetId — 서버가 companyMember.companyId로 조회하므로 회사 id를 담는다.
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: string; name: string; email: string }>
  >([]);
  const [priority, setPriority] = useState<Priority>('normal');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // 조직(구독) 목록
  const { data: orgsData } = useQuery({
    queryKey: ['admin', 'subscriptions', 'all'],
    queryFn: () => adminApi.getSubscriptions({ limit: 1000 }),
  });
  const organizations = orgsData?.data ?? [];

  // 사용자 목록 — 검색은 서버 `search`로 한다. 종전엔 첫 50명을 받아 그 안에서만 걸러
  // 51번째부터는 고를 수 없었다(2026-10-30).
  const debouncedUserSearch = useDebouncedValue(userSearch.trim());
  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users', 'message-targets', debouncedUserSearch],
    queryFn: () =>
      adminApi.getUsers({ limit: MESSAGE_TARGET_LIMIT, search: debouncedUserSearch || undefined }),
    enabled: targetType === 'users',
  });
  const users = usersData?.data ?? [];
  const usersTotal = usersData?.meta?.total ?? users.length;

  // 대상 타입 변경 시 선택 초기화
  useEffect(() => {
    setSelectedCompanyId('');
    setSelectedUsers([]);
    setUserSearch('');
  }, [targetType]);


  const toggleUser = (user: (typeof users)[0]) => {
    const email =
      user.auths?.find((a) => a.provider === 'EMAIL')?.providerId ?? '';
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.id === user.id);
      if (exists) return prev.filter((u) => u.id !== user.id);
      return [...prev, { id: user.id, name: getActorDisplayName({ name: user.member?.name, deletedAt: user.deletedAt }, '-'), email }];
    });
  };

  const isValid = () => {
    if (!title.trim() || !message.trim()) return false;
    if (targetType === 'organization' && !selectedCompanyId) return false;
    if (targetType === 'users' && selectedUsers.length === 0) return false;
    return true;
  };

  const targetLabel =
    targetType === 'all'
      ? '전체 사용자'
      : targetType === 'organization'
        ? `조직 "${organizations.find((o) => o.companyId === selectedCompanyId)?.name}"`
        : `선택된 ${selectedUsers.length}명의 사용자`;

  const handleSendClick = () => {
    if (!isValid()) return;
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    setSending(true);
    try {
      const res = await adminApi.sendAdminMessage({
        targetType,
        targetId: targetType === 'organization' ? selectedCompanyId : undefined,
        userIds:
          targetType === 'users'
            ? selectedUsers.map((u) => u.id)
            : undefined,
        title: title.trim(),
        message: message.trim(),
        priority,
      });

      if (res.success && res.data) {
        showToast(
          `${res.data.targetDescription}에게 메시지를 발송했습니다.`,
          'success',
        );
        setTitle('');
        setMessage('');
        setSelectedUsers([]);
      }
    } catch (err) {
      const { message: errMsg } = extractApiError(err);
      showToast(errMsg, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">메시지 발송</h2>

      <div className="bg-white rounded-card-lg shadow p-6 max-w-2xl">
        {/* 발송 대상 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            발송 대상
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="targetType"
                value="all"
                checked={targetType === 'all'}
                onChange={() => setTargetType('all')}
                className="text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">전체 사용자</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="targetType"
                value="organization"
                checked={targetType === 'organization'}
                onChange={() => setTargetType('organization')}
                className="text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">특정 조직</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="targetType"
                value="users"
                checked={targetType === 'users'}
                onChange={() => setTargetType('users')}
                className="text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">특정 사용자</span>
            </label>
          </div>

          {/* 조직 선택 */}
          {targetType === 'organization' && (
            <div className="mt-3">
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">조직을 선택하세요</option>
                {organizations.map((org) => (
                  // value는 회사 id — 서버가 targetId를 companyMember.companyId로 조회한다.
                  <option key={org.id} value={org.companyId}>
                    {org.name} ({org._count.members}명)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 사용자 선택 */}
          {targetType === 'users' && (
            <div className="mt-3 space-y-3">
              {/* 선택된 사용자 태그 */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs"
                    >
                      {u.name}
                      <button
                        onClick={() =>
                          setSelectedUsers((prev) =>
                            prev.filter((p) => p.id !== u.id),
                          )
                        }
                        className="hover:text-red-900"
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                type="text"
                placeholder="이름·이메일·전화·아이디로 검색..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />

              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-card">
                {users.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    사용자가 없습니다.
                  </div>
                ) : (
                  users.map((user) => {
                    const email =
                      user.auths?.find((a) => a.provider === 'EMAIL')
                        ?.providerId ?? '';
                    const isSelected = selectedUsers.some(
                      (u) => u.id === user.id,
                    );
                    return (
                      <label
                        key={user.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-red-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleUser(user)}
                          className="text-red-600 focus:ring-red-500 rounded"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {getActorDisplayName({ name: user.member?.name, deletedAt: user.deletedAt }, '-')}
                          </div>
                          {email && (
                            <div className="text-xs text-gray-500 truncate">
                              {email}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              {usersTotal > users.length && (
                <p className="text-xs text-gray-500">
                  {debouncedUserSearch ? '검색 결과' : '전체'} {usersTotal}명 중 {users.length}명 표시 — 검색으로 좁혀 주세요.
                </p>
              )}
              <p className="text-xs text-gray-500">
                선택: {selectedUsers.length}명
              </p>
            </div>
          )}
        </div>

        {/* 메시지 유형 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            메시지 유형
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="priority"
                value="normal"
                checked={priority === 'normal'}
                onChange={() => setPriority('normal')}
                className="text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                일반{' '}
                <span className="text-gray-400">(웹소켓 알림만)</span>
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="priority"
                value="important"
                checked={priority === 'important'}
                onChange={() => setPriority('important')}
                className="text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                중요{' '}
                <span className="text-gray-400">
                  (웹소켓 + 웹 푸시 알림)
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* 제목 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            제목
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="메시지 제목을 입력하세요"
            maxLength={100}
            className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* 내용 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            내용
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="메시지 내용을 입력하세요"
            rows={5}
            maxLength={1000}
            className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-vertical"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {message.length}/1000
          </p>
        </div>

        {/* 발송 버튼 */}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSendClick}
            disabled={!isValid() || sending}
          >
            {sending ? '발송 중...' : '발송하기'}
          </Button>
        </div>
      </div>

      {/* 발송 확인 모달 */}
      <Modal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="메시지 발송 확인"
        size="sm"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">대상</span>
              <span className="font-medium text-gray-900">{targetLabel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">유형</span>
              <span className="font-medium text-gray-900">
                {priority === 'important' ? (
                  <span className="text-red-600">중요 (웹소켓 + 웹푸시)</span>
                ) : (
                  '일반 (웹소켓)'
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">제목</span>
              <span className="font-medium text-gray-900 truncate ml-4 max-w-[200px]">
                {title}
              </span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-card p-3">
            <p className="text-xs text-gray-500 mb-1">내용 미리보기</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-4">
              {message}
            </p>
          </div>

          <p className="text-sm text-gray-500 text-center">
            위 내용으로 메시지를 발송하시겠습니까?
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => setShowConfirm(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-control hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              취소
            </button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirmSend}
              className="flex-1"
            >
              발송하기
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
